// src/components/analytics/QrAnalyticsDashboard.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Box,
  Heading,
  Button,
  Flex,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  Tooltip,
  SimpleGrid,
  Divider,
} from "@chakra-ui/react";

import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";

import { exportToCSV } from "../../utils/exportToCSV";
import { exportToPDF } from "../../utils/exportToPDF";
import { supabase } from "../../utils/supabaseClient";
import useBusinessGuard from "../../hooks/useBusinessGuard";
import UpgradeToProModal from "../billing/UpgradeToProModal.jsx";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ChartTooltip,
  Legend
);

const BRAND_RED = "#E63946";
const BRAND_RED_SOFT = "rgba(230, 57, 70, 0.7)";
const BRAND_RED_FADE = "rgba(230, 57, 70, 0.15)";
const ANALYTICS_PLANS = ["pro", "growth", "agency"];

export default function QrAnalyticsDashboard({ user: userProp }) {
  const toast = useToast();
  const printRef = useRef(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    user: guardUser,
    profile,
    loading: guardLoading,
  } = useBusinessGuard();

  const activeUser = userProp || guardUser;

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [slots, setSlots] = useState([]);
  const [timeseries, setTimeseries] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);
  const [isUpgradeOpen, setUpgradeOpen] = useState(false);

  const currentPlan = String(profile?.plan || "free").toLowerCase();
  const hasAccess = ANALYTICS_PLANS.includes(currentPlan);

  useEffect(() => {
    if (guardLoading || !activeUser?.id) return;

    let cancelled = false;

    async function loadAnalytics() {
      setLoading(true);
      setErrorMsg(null);

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        const token = session?.access_token;
        if (!token) throw new Error("Missing auth token");

        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/qr-analytics`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ userId: activeUser.id }),
          }
        );

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || "Failed to load analytics");
        }

        const data = await res.json();

        if (!cancelled) {
          setSummary(data.summary || {});
          setSlots(data.slots || []);
          setTimeseries(data.timeseries || []);
        }
      } catch (err) {
        if (!cancelled) {
          setErrorMsg(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadAnalytics();

    return () => {
      cancelled = true;
    };
  }, [activeUser?.id, guardLoading]);

  const handleUpgrade = async () => {
    if (!activeUser?.id) {
      toast({
        title: "Upgrade failed",
        description: "No signed-in user was found.",
        status: "error",
      });
      return;
    }

    const { data, error } = await supabase.functions.invoke(
      "create-pro-checkout-session"
    );

    if (error) {
      toast({
        title: "Upgrade failed",
        description: error.message,
        status: "error",
      });
      return;
    }

    if (!data?.url) {
      toast({
        title: "Upgrade failed",
        description: "Checkout did not return a redirect URL.",
        status: "error",
      });
      return;
    }

    window.location.href = data.url;
  };

  if (guardLoading || loading) {
    return (
      <Box p={{ base: 4, md: 6 }}>
        <Heading size="lg" mb={2}>📊 QR Analytics</Heading>
        <Text color="gray.500">Loading analytics…</Text>
      </Box>
    );
  }

  if (errorMsg) {
    return (
      <Box p={{ base: 4, md: 6 }}>
        <Heading size="lg" mb={2}>📊 QR Analytics</Heading>
        <Text color="red.500">{errorMsg}</Text>
      </Box>
    );
  }

  const totalImpressions = hasAccess ? summary?.impressions || 0 : 0;
  const totalScans = hasAccess ? summary?.scans || 0 : 0;
  const ctr = totalImpressions > 0 ? (totalScans / totalImpressions) * 100 : 0;

  const safeSlots = hasAccess ? slots : [];
  const safeTimeseries = hasAccess ? timeseries : [];

  const barLabels = safeSlots.map((slot) => `#${slot.slot_number}`);
  const barImpressions = safeSlots.map((slot) => slot.impressions || 0);
  const barScans = safeSlots.map((slot) => slot.scans || 0);

  const barData = {
    labels: barLabels,
    datasets: [
      {
        label: "Impressions",
        data: barImpressions,
        backgroundColor: BRAND_RED_FADE,
        borderColor: BRAND_RED,
        borderWidth: 1.5,
        borderRadius: 6,
      },
      {
        label: "Scans",
        data: barScans,
        backgroundColor: BRAND_RED_SOFT,
        borderColor: BRAND_RED,
        borderWidth: 1.5,
        borderRadius: 6,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", labels: { color: "#4A5568" } },
      tooltip: { mode: "index", intersect: false },
    },
    scales: {
      x: { ticks: { color: "#4A5568" }, grid: { display: false } },
      y: { ticks: { color: "#A0AEC0" }, grid: { color: "rgba(0,0,0,0.04)" } },
    },
  };

  const lineLabels = safeTimeseries.map((point) => point.date);
  const lineScans = safeTimeseries.map((point) => point.scans || 0);

  const lineData = {
    labels: lineLabels,
    datasets: [
      {
        label: "Scans over time",
        data: lineScans,
        borderColor: BRAND_RED,
        backgroundColor: BRAND_RED_FADE,
        tension: 0.35,
        fill: true,
        pointRadius: 3,
        pointBackgroundColor: BRAND_RED,
      },
    ],
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: "index", intersect: false },
    },
    scales: {
      x: { ticks: { color: "#4A5568" }, grid: { display: false } },
      y: { ticks: { color: "#A0AEC0" }, grid: { color: "rgba(0,0,0,0.04)" } },
    },
  };

  return (
    <Box p={{ base: 4, md: 6 }} width="100%" overflowX="hidden">
      <Heading size="lg" mb={2}>📊 QR Analytics Dashboard</Heading>
      <Text color="gray.600" mb={4}>
        See how your QR slots are performing across your flyers.
      </Text>

      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        mb={4}
        gap={3}
      >
        <Button
          onClick={onOpen}
          colorScheme="gray"
          variant="outline"
          isDisabled={!hasAccess}
          width={{ base: "100%", md: "auto" }}
          whiteSpace="normal"
        >
          Print Preview & Export PDF
        </Button>

        {!hasAccess && (
          <Button
            colorScheme="red"
            onClick={() => setUpgradeOpen(true)}
            width={{ base: "100%", md: "auto" }}
            whiteSpace="normal"
          >
            Unlock real analytics with PRO
          </Button>
        )}
      </Flex>

      {hasAccess && (
        <Badge colorScheme="green" mb={2}>
          {currentPlan.toUpperCase()} PLAN ACTIVE
        </Badge>
      )}

      <UpgradeToProModal
        isOpen={isUpgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        onUpgrade={handleUpgrade}
      />

      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent mx={{ base: 3, md: 6 }}>
          <ModalHeader>🖨️ Print Preview</ModalHeader>
          <ModalBody overflowX="auto">
            <Box ref={printRef} id="qr-dashboard-preview" p={4} minW="700px">
              <Heading size="md" mb={4}>Summary</Heading>
              <SimpleGrid columns={[1, 3]} spacing={4} mb={6}>
                <Stat><StatLabel>Total Impressions</StatLabel><StatNumber>{totalImpressions}</StatNumber></Stat>
                <Stat><StatLabel>Total Scans</StatLabel><StatNumber>{totalScans}</StatNumber></Stat>
                <Stat><StatLabel>Scan Rate</StatLabel><StatNumber>{ctr.toFixed(1)}%</StatNumber></Stat>
              </SimpleGrid>

              <Heading size="md" mb={3}>Top Slots</Heading>
              <Table size="sm">
                <Thead><Tr><Th>Slot</Th><Th>Business</Th><Th>Sector</Th><Th isNumeric>Impressions</Th><Th isNumeric>Scans</Th><Th isNumeric>Rate</Th></Tr></Thead>
                <Tbody>
                  {safeSlots.map((slot) => {
                    const impressions = slot.impressions || 0;
                    const scans = slot.scans || 0;
                    const rate = impressions > 0 ? (scans / impressions) * 100 : 0;
                    return (
                      <Tr key={slot.slot_id}>
                        <Td><Badge colorScheme="red">#{slot.slot_number}</Badge></Td>
                        <Td>{slot.business_name || "Advert"}</Td>
                        <Td>{slot.postcode_sector}</Td>
                        <Td isNumeric>{impressions}</Td>
                        <Td isNumeric>{scans}</Td>
                        <Td isNumeric>{rate.toFixed(1)}%</Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          </ModalBody>
          <ModalFooter flexWrap="wrap" gap={2}>
            <Button
              onClick={() => exportToPDF("qr-dashboard-preview", "qr_dashboard")}
              colorScheme="red"
              isDisabled={!hasAccess}
            >
              Export PDF
            </Button>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Divider my={6} />

      <Tabs variant="enclosed" colorScheme="red" isFitted>
        <TabList><Tab>Overview</Tab><Tab>Top Slots</Tab></TabList>
        <TabPanels>
          <TabPanel px={{ base: 0, md: 4 }}>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
              {[['Total Impressions', totalImpressions], ['Total Scans', totalScans], ['Scan Rate', `${ctr.toFixed(1)}%`]].map(([label, value]) => (
                <Box key={label} p={4} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor="gray.100">
                  <Stat><StatLabel color="gray.600">{label}</StatLabel><StatNumber>{value}</StatNumber></Stat>
                </Box>
              ))}
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              <Box p={4} minW={0} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor="gray.100">
                <Heading size="sm" mb={3}>Impressions vs Scans by Slot</Heading>
                {barLabels.length === 0 ? (
                  <Text color="gray.500" fontSize="sm">No slot data yet. Once your flyers go out, you’ll see slot-level performance here.</Text>
                ) : (
                  <Box height={{ base: "260px", md: "340px" }}><Bar data={barData} options={barOptions} /></Box>
                )}
              </Box>

              <Box p={4} minW={0} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor="gray.100">
                <Heading size="sm" mb={3}>Scans over time</Heading>
                {lineLabels.length === 0 ? (
                  <Text color="gray.500" fontSize="sm">No scans yet. As customers start scanning your QR codes, this chart will show trends over time.</Text>
                ) : (
                  <Box height={{ base: "260px", md: "340px" }}><Line data={lineData} options={lineOptions} /></Box>
                )}
              </Box>
            </SimpleGrid>
          </TabPanel>

          <TabPanel px={{ base: 0, md: 4 }}>
            <Flex direction={{ base: "column", sm: "row" }} justify="flex-end" mb={4} gap={2}>
              <Button onClick={() => exportToCSV("qr_slots", safeSlots)} colorScheme="red" variant="outline" isDisabled={!hasAccess} width={{ base: "100%", sm: "auto" }}>Export CSV</Button>
              <Button onClick={() => exportToPDF("qr-slots-section", "qr_slots")} isDisabled={!hasAccess} width={{ base: "100%", sm: "auto" }}>Export PDF</Button>
            </Flex>

            <Box id="qr-slots-section" overflowX="auto">
              <Table size="sm" minW="700px">
                <Thead><Tr><Th>Slot</Th><Th>Business</Th><Th>Sector</Th><Th isNumeric>Impressions</Th><Th isNumeric>Scans</Th><Th isNumeric>Rate</Th></Tr></Thead>
                <Tbody>
                  {safeSlots.length === 0 && (
                    <Tr><Td colSpan={6}><Text color="gray.500" fontSize="sm">No slot analytics yet. Once your flyer campaigns run, you’ll see your best-performing slots here.</Text></Td></Tr>
                  )}
                  {safeSlots.map((slot) => {
                    const impressions = slot.impressions || 0;
                    const scans = slot.scans || 0;
                    const rate = impressions > 0 ? (scans / impressions) * 100 : 0;
                    return (
                      <Tr key={slot.slot_id}>
                        <Td><Badge colorScheme="red">#{slot.slot_number}</Badge></Td>
                        <Td><Tooltip label={slot.business_name || ""}><Text noOfLines={1}>{slot.business_name || "Advert"}</Text></Tooltip></Td>
                        <Td>{slot.postcode_sector}</Td>
                        <Td isNumeric>{impressions}</Td>
                        <Td isNumeric>{scans}</Td>
                        <Td isNumeric>{rate.toFixed(1)}%</Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}
